// Shared event-stream helpers used by Chat.jsx and ChatV2Modal.

// streamSeq returns the canonical JetStream stream sequence on an event
// envelope. The wire shape uses snake_case `sequence_number` (per the
// JOBE-0051.2 envelope) but earlier UI code used `seq` — both forms are
// tolerated here so a mid-rollout deploy doesn't double-render.
export function streamSeq(ev) {
  if (!ev) return 0;
  const v = ev.sequence_number ?? ev.seq ?? 0;
  return Number(v) || 0;
}

// eventType normalises the envelope's type field so the UI can match
// on a single canonical name regardless of which producer emitted it.
// Recognised values include the V1 envelope set from
// src/operator/conv/envelope.go (`message.created`, `message.edited`,
// `assistant.message.delta`, etc.), the `approval.requested` /
// `approval.resolved` pair (JOBE-APPROVAL-events), plus the legacy
// bare `message` shape carried by some older WS frames.
export function eventType(ev) {
  return ev?.event_type || ev?.type || '';
}

// correlationKey returns the per-stream pairing field shared by an
// `assistant.message.delta` event and the terminal `message.created`
// envelope that follows it. The agent runtime stamps both with the
// same `correlation_id` on the envelope (see
// src/agents/runtime/loop.go `publishAssistantDelta` /
// `publishAssistantMessage`). Returns null when absent so callers can
// route the event through the normal (non-streamed) path.
export function correlationKey(ev) {
  return ev?.correlation_id || null;
}

// mergeStreamingDeltas walks the chronologically-sorted event buffer
// and collapses streaming `assistant.message.delta` events into a
// virtual "in-flight" message row keyed by correlation_id. The
// returned list contains:
//   - every non-delta event unchanged,
//   - one synthetic streaming row per correlation_id that has deltas
//     but no terminal `message.created` (actor_kind=agent) yet,
//   - no rows for deltas that have already been resolved by a
//     terminal assistant `message.created` with a matching
//     correlation_id (the final wins — see ADR-0022 §9
//     "render only the final" mode).
//
// Purity: this function is called from render, so it MUST be free of
// side effects. Gap detection lives in the WS-message pipeline so
// each gap is logged once on arrival rather than re-warned per render.
//
// Behaviour notes consumers in tests rely on:
//   - Deltas concatenate in the order they appear in the buffer.
//     Producers stamp a per-message monotonic `sequence` (0,1,2,…).
//     A gap (e.g. consumer joined mid-stream) sets `gapDetected: true`
//     on the synthetic row; rendering remains best-effort.
//   - Finality is scoped to `actor_kind === 'agent'` on a matching
//     `message.created`. Other events that happen to share the same
//     correlation_id (a user echo, a tool-call envelope, etc.) do not
//     suppress in-flight deltas.
//   - Out-of-order arrival where the terminal assistant
//     `message.created` precedes a delta is handled by suppressing
//     any deltas whose correlation_id is already finalised. The final
//     body always wins.
//   - Events without a `correlation_id` are handled by actor-scoped
//     suppression: if the delta's stream sequence is lower than the
//     highest-seq `message.created` for the same agent actor already
//     in the buffer, the delta is silently dropped (it is historical
//     and the final message already renders the content). If no such
//     finalised message exists yet (the stream is live), the uncorrelated
//     deltas accumulate under a synthetic `__anon__<actor_id>` key so
//     incremental text is visible while the agent is still writing.
export function mergeStreamingDeltas(events) {
  if (!Array.isArray(events) || events.length === 0) return events || [];

  // First pass: collect (a) finalised correlation_ids from terminal
  // agent message.created events, and (b) the highest stream sequence
  // per agent actor for the same events. (b) lets us suppress
  // un-correlated deltas that precede a known final message.
  const finalised = new Set();
  // actor_id → highest stream seq of a terminal agent message.created.
  const latestAgentMsgSeq = new Map();
  for (const ev of events) {
    const t = eventType(ev);
    if (t !== 'message.created' && t !== 'message') continue;
    if (ev?.actor_kind !== 'agent') continue;
    const key = correlationKey(ev);
    if (key) finalised.add(key);
    const actorId = ev?.actor_id || '';
    const seq = streamSeq(ev) || 0;
    if (seq > (latestAgentMsgSeq.get(actorId) || 0)) {
      latestAgentMsgSeq.set(actorId, seq);
    }
  }

  // Second pass: build the output, accumulating deltas into a single
  // streaming row per effective key and emitting it at the position of
  // the first delta seen for that key.
  const accum = new Map(); // effectiveKey -> synthetic streaming-row object
  const out = [];
  for (const ev of events) {
    const t = eventType(ev);
    if (t === 'assistant.message.delta') {
      const key = correlationKey(ev);

      // Determine the effective accumulation key. For correlated deltas
      // it is the correlation_id. For uncorrelated deltas it is a
      // synthetic actor-scoped key, or the delta is suppressed entirely
      // when a later terminal message for the same actor is in the buffer.
      let effectiveKey = key;
      if (!effectiveKey) {
        const actorId = ev?.actor_id || '';
        const latestFinalSeq = latestAgentMsgSeq.get(actorId) || 0;
        const deltaStreamSeq = streamSeq(ev) || 0;
        if (latestFinalSeq > 0 && deltaStreamSeq < latestFinalSeq) {
          // Historical delta: a later message.created already carries the
          // content for this actor. Suppress to avoid duplicate rendering.
          continue;
        }
        effectiveKey = `__anon__${actorId}`;
      }

      if (key && finalised.has(key)) {
        // Correlated delta whose final message is in the buffer — drop.
        continue;
      }

      const body = ev?.body || ev?.payload || {};
      const deltaText = typeof body.delta_text === 'string' ? body.delta_text : '';
      const seq = Number(body.sequence ?? 0) || 0;
      if (!accum.has(effectiveKey)) {
        const row = {
          // synthetic event row; mirrors the envelope shape just
          // enough that EventRow can render it.
          __streaming: true,
          event_type: 'assistant.message.streaming',
          correlation_id: effectiveKey,
          ts: ev?.ts,
          actor_id: ev?.actor_id,
          actor_kind: ev?.actor_kind,
          sequence_number: ev?.sequence_number,
          text: deltaText,
          lastSeq: seq,
          // gapDetected: true if any delta after sequence 0 arrived
          // without a predecessor. JetStream is durable so this is
          // rare under normal conditions — most often surfaces when
          // the consumer joined mid-stream.
          gapDetected: seq !== 0,
        };
        out.push(row);
        accum.set(effectiveKey, row);
      } else {
        const row = accum.get(effectiveKey);
        // Gap detection: any non-contiguous sequence beyond the
        // initial one signals a dropped delta. Render best-effort.
        if (seq !== row.lastSeq + 1 && seq !== row.lastSeq) {
          row.gapDetected = true;
        }
        row.text = row.text + deltaText;
        row.lastSeq = seq;
      }
      continue;
    }
    out.push(ev);
  }
  return out;
}
